import { createActionCreator, createReducer } from 'deox';
import produce, { Draft } from 'immer';
import { Effects, Effect, EffectsWithTime, TextEffect } from 'types/Effects';
import { AppState } from 'types/AppState';
import { FontStyleTypeEnum } from '@picsart/rc/types/containerComponents/Font';
import { createSelector } from 'reselect';
import { getScenes } from './scenes';
import { Scene } from 'types/Scene';
import { clamp } from 'ramda';
import {
  ITextData,
  IFontItem,
  IPackageItem
} from '@picsart/rc/types/containerComponents/Sidebar/types';
import { BASE_SIZE } from 'constants/canvas';
import { ITextStyle } from '@picsart/rc/types/utils/types';

const roundEffectNumbers = (source: Partial<TextEffect>) => {
  const eff: Partial<TextEffect> = { ...source };
  if ('fontSize' in source) eff.fontSize = Math.round(source.fontSize);
  if ('position' in source) eff.position.x = Math.round(source.position.x);
  if ('position' in source) eff.position.y = Math.round(source.position.y);
  if ('width' in source) eff.width = Math.round(source.width);
  if ('height' in source) eff.height = Math.round(source.height);
  return eff;
};

const defaultState = {};

export const addEffectToScene = createActionCreator(
  'EFFECTS/ADD_EFFECT',
  resolve => (effect: Effect, sceneId: string) => resolve({ effect, sceneId })
);
export const addEffectsToScene = createActionCreator(
  'EFFECTS/ADD_EFFECTS',
  resolve => (effects: Effect[], sceneId: string) => resolve({ effects, sceneId })
);
export const deleteEffectsFromScene = createActionCreator(
  'EFFECTS/DELETE_EFFECTS',
  resolve => (ids: string[], sceneId: string) => resolve({ ids, sceneId })
);
export const updateEffectFontSetting = createActionCreator(
  'EFFECTS/CHANGE_EFFECT_FONT_SETTING',
  resolve => (sceneId: string, effectId: string, active: boolean, type: FontStyleTypeEnum) =>
    resolve({ sceneId, effectId, active, type })
);
export const duplicateEffects = createActionCreator(
  'EFFECTS/DUPLICATE',
  resolve => (effectIds: string[], sceneId: string) => resolve({ effectIds, sceneId })
);

export const addTextbox = createActionCreator(
  'EFFECTS/ADD_TEXTBOX',
  resolve => ({
    text,
    name,
    fontItem,
    font_bold,
    textSettings
  }: Pick<ITextData, 'name' | 'text' | 'font_bold'> & {
    fontItem?: IFontItem & { data_url?: string };
    textSettings?: ITextStyle['text_settings'] & { shadow_blur?: number };
  }) => resolve({ text, name, fontItem, font_bold, textSettings })
);

export const addTextTemplate = createActionCreator(
  'EFFECTS/ADD_TEXT_TEMPLATE',
  resolve => (presetsData: IPackageItem) => resolve(presetsData)
);

export const deleteScene = createActionCreator('SCENE/DELETE', resolve => (id: string) =>
  resolve(id)
);
export const deleteAllEffects = createActionCreator('EFFECTS/DELETE_ALL');

export const updateScenesEffectsFont = createActionCreator(
  'EFFECTS/UPDATE_SCENES_EFFECTS_FONT',
  resolve => (scale: number) => resolve({ scale })
);

export const updateEffectParams = createActionCreator(
  'EFFECTS/UPDATE',
  resolve => (sceneId: string, effectId: string, effectParams: Partial<Effect>) =>
    resolve({ sceneId, effectId, effectParams })
);

export const updateAllEffects = createActionCreator(
  'EFFECTS/UPDATE_ALL_FONTS',
  resolve => (effects: Effects) => resolve(effects)
);

export const updateEffectsParams = createActionCreator(
  'EFFECTS/UPDATE_EFFECTS',
  resolve => (sceneId: string, effectParams: Partial<Effect>[]) =>
    resolve({ sceneId, effectParams })
);

export const effects = createReducer(defaultState, handle => [
  handle(
    addEffectToScene,
    produce<(d: Draft<Effects>, a: ReturnType<typeof addEffectToScene>) => void>(
      (draft, { payload: { effect, sceneId } }) => {
        const effectsArray = draft[sceneId];
        const eff = {
          ...effect,
          ...roundEffectNumbers(effect)
        };

        if (effectsArray) {
          effectsArray.push(eff);
        } else {
          draft[sceneId] = [eff];
        }
      }
    )
  ),
  handle(
    addEffectsToScene,
    produce<(d: Draft<Effects>, a: ReturnType<typeof addEffectsToScene>) => void>(
      (draft, { payload: { effects, sceneId } }) => {
        const effectsArray = draft[sceneId];
        if (effectsArray) {
          draft[sceneId] = [...effectsArray, ...effects];
        } else {
          draft[sceneId] = effects;
        }
      }
    )
  ),
  handle(
    deleteEffectsFromScene,
    produce<(d: Draft<Effects>, a: ReturnType<typeof deleteEffectsFromScene>) => void>(
      (draft, { payload: { ids, sceneId } }) => {
        draft[sceneId] = draft[sceneId]?.filter(item => !ids.includes(item.id));
      }
    )
  ),
  handle(
    updateEffectFontSetting,
    produce<(d: Draft<Effects>, a: ReturnType<typeof updateEffectFontSetting>) => void>(
      (draft, { payload: { sceneId, effectId, active, type } }) => {
        const effectsArray = draft[sceneId];
        const effect = effectsArray?.find(item => item.id === effectId);
        if (effect) {
          const settings = effect.settings;

          if (active) {
            if (!settings.includes(type)) settings.push(type);
          } else {
            effect.settings = settings.filter(item => item !== type);
          }
        }
      }
    )
  ),
  handle(
    deleteScene,
    produce<(d: Draft<Effects>, a: ReturnType<typeof deleteScene>) => void>(
      (draft, { payload: id }) => {
        delete draft[id];
      }
    )
  ),
  handle(updateScenesEffectsFont, (state: Effects, { payload: { scale } }) => {
    const snapState = {
      ...state
    };

    Object.keys(snapState).forEach(sceneId => {
      snapState[sceneId] = snapState[sceneId].map(item => {
        const fullY = BASE_SIZE - item.height;
        const yP = clamp(0, 1, item.position.y / fullY);

        return {
          ...item,
          ...roundEffectNumbers({
            position: {
              x: item.position.x * scale,
              y: item.position.y + (item.height - item.height * scale) * yP
            },
            fontSize: item.fontSize * scale,
            width: item.width * scale,
            height: item.height * scale
          })
        };
      });
    });

    return snapState;
  }),
  handle(deleteAllEffects, () => ({})),
  handle(
    updateEffectParams,
    produce<(d: Draft<Effects>, a: ReturnType<typeof updateEffectParams>) => void>(
      (draft, { payload: { sceneId, effectId, effectParams } }) => {
        const effectsArray = draft[sceneId];
        const effect = effectsArray?.find(item => item.id === effectId);
        if (effect) {
          Object.assign(effect, roundEffectNumbers(effectParams));
        }
      }
    )
  ),
  handle(updateAllEffects, (_, { payload }) => payload),
  handle(updateEffectsParams, (state: Effects, { payload: { sceneId, effectParams } }) => {
    return state[sceneId]
      ? {
          ...state,
          [sceneId]: state[sceneId].map((item: Effect) => {
            const ep = effectParams.find(({ id }) => item.id === id);
            return ep ? { ...item, ...roundEffectNumbers(ep) } : item;
          })
        }
      : state;
  })
]);

export const getEffects = (state: AppState): Effects => state.effects;

export const getEffectById = (id: string) => (state: AppState): Effect => {
  for (const arrEffects of Object.values(state.effects)) {
    const effect = arrEffects.find(e => e.id === id);
    if (effect) return effect;
  }
};

export const isFontPaid = (state: AppState): boolean =>
  Object.values(state.effects).some(effect => effect.some(e => e.font.isPaid));

export const getSceneIdsWithPaidFonts = createSelector<AppState, Effects, Scene['id'][]>(
  getEffects,
  effects => Object.keys(effects).filter(key => effects[key].some(effect => effect.font.isPaid))
);

export const getEffectsWithTime = createSelector<AppState, Effects, Scene[], EffectsWithTime[]>(
  getEffects,
  state => getScenes(state),
  (allEffects, allScenes) =>
    allScenes
      .map(scene => {
        const sceneEffects = allEffects[scene.id] ?? [];
        return sceneEffects.map(e => ({
          ...e,
          timeStart: 0,
          timeEnd: scene.duration,
          sceneWidth: scene.size.width,
          sceneHeight: scene.size.height,
          scalingMode: scene.scalingMode,
          sceneId: scene.id
        }));
      })
      .flat()
);

export const getActiveEffects = createSelector<AppState, Effects, string[], string, Effect[]>(
  getEffects,
  state => state.currentTextId,
  state => state.scenes.current,
  (allEffects, textIds, currentScene) => {
    return allEffects[currentScene]?.filter(({ id }: Effect) => textIds.includes(id)) || [];
  }
);
// we don't have global effects now, but let it stay as a stub
const getGlobalEffects = (): TextEffect[] => [];

export const getGlobalEffectsWithTime = createSelector<
  AppState,
  TextEffect[],
  Scene[],
  EffectsWithTime[]
>(
  getGlobalEffects,
  state => getScenes(state),
  (allEffects, allScenes) =>
    allScenes
      .map(scene => {
        return allEffects.map(e => ({
          ...e,
          timeStart: scene.start,
          timeEnd: scene.start + scene.duration,
          sceneWidth: scene.size.width,
          sceneHeight: scene.size.height,
          scalingMode: scene.scalingMode,
          sceneId: scene.id
        }));
      })
      .flat()
);
